import { 
  ArrowRightLeft, 
  CheckCircle2, 
  XCircle
} from 'lucide-react'
import DataToolTip from '@/components/common/DataToolTip';
import './styles.scss'

export default function TransferMatrix({ mappings, basName }) {
  // mapping structure: { sourceCourse, targetCourse, overlap, credits, status }
  const data = mappings || [];

  // Calculate direct mapped course credits (e.g. 12 credits for the 4 English courses)
  // We only exclude the general block transfer row (which has mappingType 'BLOCK' and no source course code)
  const directTransferCredits = data
    .filter(row => !(row.mappingType === 'BLOCK' && !row.sourceCourseCode))
    .reduce((sum, row) => {
      const credits = row.creditsTransferred != null ? row.creditsTransferred : (row.credits || 0);
      return sum + credits;
    }, 0);

  const blockCredits = Math.max(0, 60 - directTransferCredits);

  // Total approved transfer credits from completed AAS degree is exactly 60!
  const totalApprovedCredits = 60;

  const totalCoursesApproved = data.length;

  return (
    <div className="transfer-matrix-glass">

      {/* Premium Transfer Summary Stats */}
      <div className="transfer-summary-stats-bar">
        <div className="stat-card">
          <span className="stat-label">Total Mapped Credits</span>
          <span className="stat-value">{totalApprovedCredits} <span className="stat-unit">Credits</span></span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Courses Approved</span>
          <span className="stat-value">{totalCoursesApproved} <span className="stat-unit">Courses</span></span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Articulation Goal</span>
          <span className="stat-value bas-goal" title={basName || 'BAS Degree'}>{basName || 'BAS Degree'}</span>
        </div>
      </div>

      <div className="matrix-header">
        <div className="header-cell">Source AAS Course</div>
        <div className="header-cell connector-gap"></div>
        <div className="header-cell">Transfer Target (BAS)</div>
        <div className="header-cell">
          <DataToolTip text="The number of credits officially awarded toward the BAS degree. 'TBD' means the count is pending final institutional review.">
            Credits Awarded
          </DataToolTip>
        </div>
        <div className="header-cell">Status</div>
      </div>
      <div className="matrix-body">
        {data.map((row, idx) => {
          const getFriendlyBlockTitle = (code, defaultTitle) => {
            if (!code) return defaultTitle;
            const clean = code.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
            if (clean.includes('BASSPECIALIZATION')) return 'BAS Specialization Requirement';
            if (clean.includes('BASELECTIVE')) return 'Upper Division Elective Requirement';
            if (clean.includes('BASGENED')) return 'General Education Core Requirement';
            if (clean.includes('BASCORE')) return 'Upper Division Core Requirement';
            return defaultTitle;
          };

          const cleanTargetCode = row.targetCourseCode ? row.targetCourseCode.trim().toUpperCase().replace(/[^A-Z0-9]/g, '') : '';
          const isTargetBlock = cleanTargetCode.includes('BASSPECIALIZATION') || 
                                cleanTargetCode.includes('BASELECTIVE') || 
                                cleanTargetCode.includes('BASGENED') || 
                                cleanTargetCode.includes('BASCORE');

          const isPending = !isTargetBlock && (row.sourceCourseTitle === 'Course title pending...' || row.targetCourseTitle === 'Course title pending...');
          
          const source = row.sourceCourseTitle === 'Course title pending...' ? 
                         <span className="pending-state">Pending Intelligence...</span> : 
                         (row.sourceCourseTitle || row.sourceCourseCode || row.aasProgramName || 'Block Transfer');
          
          let target = row.targetCourseTitle;
          if (isTargetBlock) {
            target = getFriendlyBlockTitle(row.targetCourseCode, 'Upper Division Core Requirement');
          } else if (target === 'Course title pending...') {
            target = <span className="pending-state">Pending Intelligence...</span>;
          } else {
            target = target || row.targetCourseCode || row.basConcentrationName || basName || 'BAS Core';
          }
          
          const credits = row.creditsTransferred != null ? row.creditsTransferred : (row.credits || 0);
          
          const isGeneralBlockMapping = row.mappingType === 'BLOCK' && !row.sourceCourseCode;
          const cleanMappingType = row.mappingType ? row.mappingType.toUpperCase() : '';
          const isDirectOrGenEd = cleanMappingType === 'DIRECT' || cleanMappingType === 'GEN_ED';

          return (
            <div key={idx} className={`matrix-row approved ${isPending ? 'pending-row' : ''} ${isGeneralBlockMapping ? 'block-row' : ''}`}>
              <div className="cell source-cell">
                <DataToolTip text={row.sourceDescription || "Completed AAS program curriculum course requirement."}>
                  <div className="course-badge">
                    <span className="code">{row.sourceCourseCode || 'AAS CORE'}</span>
                    <span className="title">{source}</span>
                  </div>
                </DataToolTip>
              </div>
              <div className="cell connector">
                <ArrowRightLeft size={16} />
              </div>
              <div className="cell target-cell">
                <DataToolTip text={row.targetDescription || "Official curriculum requirement mapped under the articulation agreement."}>
                  <div className={`course-badge ${isDirectOrGenEd ? 'secondary' : 'warning'}`}>
                    <span className="code">{row.targetCourseCode || 'BLOCK'}</span>
                    <span className="title">{target}</span>
                  </div>
                </DataToolTip>
              </div>
              <div className="cell credits-cell">
                {isGeneralBlockMapping ? (
                  <strong>{blockCredits}</strong>
                ) : credits > 0 ? (
                  <strong>{credits}</strong>
                ) : (
                  <span className="tbd-badge">TBD Credits</span>
                )}
              </div>
              <div className="cell status-cell">
                <span className="status-tag success">
                  <CheckCircle2 size={12} /> Approved
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  )
}
